import React, { useEffect } from 'react';
import styles from './index.module.scss';
// import AgTable from '@navi/web-ui/lib/components/AgTable/AgTable';
const AgTable = React.lazy(
  () => import(/* webpackChunkName: ['table'] */ '@navi/web-ui/lib/components/AgTable/AgTable')
);
import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { getWAInteractions as fetchWAInteractions } from '../../actions/interactionsActions';
import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { NO_FEEDBACK_HISTORY_TEXT } from '../../constants/Overview.constant';
import { isObjectEmpty } from 'src/utils/commonUtils';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import { QueryParams } from 'src/components/interfaces';
import { WA_INTERACTIONS_TABLE } from '../../constants/Interactions.constant';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import TableLoader from 'src/components/TableLoader/TableLoader';
import { IWAInteractionPayload } from '../../interfaces/Interactions.type';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import { defaultPageSize } from '../../../Cases/constants/CasesConstants';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import cx from 'classnames';
import { DateFormat, dateFormat } from '@cp/src/utils/DateHelper';
import EllipsisText from '@cp/src/components/EllipsisText';
import DateTime from '../DateTime';

const WAINTERACTIONS_COL_DEFS = [
  {
    field: 'messageTimestamp',
    headerName: 'Date & Time',
    cellRenderer: (params: ICellRendererParams) => {
      const istDate = new Date(params.value);
      return <DateTime istDate={istDate} />;
    },
    width: '140px'
  },
  {
    field: 'agentName',
    headerName: 'Agent Name',
    width: '150px',
    wrapText: true,
    cellStyle: { wordBreak: 'break-word', padding: '8', cursor: 'auto' }
  },
  {
    field: 'agentNumber',
    headerName: 'Agent Number',
    width: '130px',
    cellStyle: { cursor: 'auto' },
    cellRenderer: (params: ICellRendererParams) => {
      const phoneNumber = params.data?.agentNumber;
      return <div className="rr-block">{phoneNumber}</div>;
    }
  },
  {
    field: 'messageText',
    headerName: 'Message',
    width: '320px',
    autoHeight: true,
    wrapText: true,
    cellStyle: { wordBreak: 'break-word', cursor: 'auto', padding: '8' }
  },
  {
    field: 'source',
    headerName: 'Source',
    cellStyle: { cursor: 'auto' },
    width: '110px'
  }
];

const Interactions = () => {
  const { loanId = '', customerId = '', caseReferenceId = '' } = useParams();
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );
  const { data: waInteractionsData, loading: waInteractionsLoading } =
    pageData?.waInteractions || {};
  const [searchParams] = useSearchParams();
  const { [WA_INTERACTIONS_TABLE]: queryParams } = readQueryParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const createPayloadForWAInteractions = () => {
    const { [WA_INTERACTIONS_TABLE]: queryParams } = readQueryParams();
    return {
      page: queryParams?.page || 0,
      size: queryParams?.size || defaultPageSize,
      customerReferenceId: customerId
    } as IWAInteractionPayload;
  };

  const getWAInteractionsData = () => {
    const payload = createPayloadForWAInteractions();
    dispatch(fetchWAInteractions(loanId, customerId, payload, caseReferenceId));
  };

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_NaviWAPageLand);
  });

  useEffect(() => {
    if (!pageData?.waInteractions) {
      getWAInteractionsData();
    }
  }, [pageData?.waInteractions]);

  useEffect(() => {
    if (!isObjectEmpty(queryParams)) {
      getWAInteractionsData();
    }
  }, [searchParams]);

  const updateQueryParamsAndNavigate = (params?: QueryParams, lastUsed?: string) => {
    const url = createQueryParams({
      [WA_INTERACTIONS_TABLE]: { ...queryParams, ...params, lastUsed }
    });
    navigate(url, {
      replace: true
    });
  };

  const getCurrentPageNumber = (): number => {
    return Number(queryParams?.page) + 1 || 1;
  };

  const getCurrentPageSize = (): number => {
    return Number(queryParams?.size) || defaultPageSize;
  };

  const handlePageSizeChange = (size: number) =>
    updateQueryParamsAndNavigate({ size: size.toString(), page: 0 });

  const handlePageChange = (page: number) => {
    updateQueryParamsAndNavigate({ page: (page - 1).toString() });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_PaginationClick, {
      tab: 'Navi Whatsapp'
    });
  };
  return (
    <div className={styles.waInteractionsWrapper}>
      <div className={styles.table}>
        <SuspenseLoader
          loading={!!waInteractionsLoading}
          fallBack={<TableLoader numberOfLines={6} />}
        >
          <AgTable
            defaultColDef={{
              suppressMovable: true,
              wrapText: true
            }}
            style={{
              height: `${window?.innerHeight - 369}`
            }}
            className="teamLeadTable"
            gridOptions={{ suppressCellSelection: true }}
            rowData={waInteractionsData?.content}
            columnDefs={WAINTERACTIONS_COL_DEFS}
            sizeColumnsToFit
            theme={'alpine'}
            noRowsOverlayComponent={() => (
              <Typography variant="p4" className={styles.emptyListText}>
                {NO_FEEDBACK_HISTORY_TEXT}
              </Typography>
            )}
            animateRows
            paginationWrapperClasses={'paginationWrapperClasses'}
            PaginationComponent={
              <Pagination
                totalCount={waInteractionsData?.totalElements || 0}
                onPageChange={handlePageChange}
                currentPage={getCurrentPageNumber()}
                onPageSizeChange={handlePageSizeChange}
                pageSize={getCurrentPageSize()}
                pageNumberDropDownPosition={DropDownPosition.TOP}
              />
            }
          />
        </SuspenseLoader>
      </div>
    </div>
  );
};
export default Interactions;
